<!-- ImageCropperModal.vue -->
<script setup lang="ts">
import { ref } from "vue";
import { Cropper, type CropperResult } from "vue-advanced-cropper";
import "vue-advanced-cropper/dist/style.css";
import CustomButton from "@/core/components/CustomButton.vue";
import { useI18n } from "vue-i18n";

const props = withDefaults(
  defineProps<{
    imageSrc: string;
    maxWidth: number;
    maxHeight: number;
  }>(),
  {
    maxWidth: 480,
    maxHeight: 480,
  },
);

const emit = defineEmits<{
  confirm: [file: File];
  cancel: [];
}>();

const { t } = useI18n();
const cropperRef = ref<InstanceType<typeof Cropper> | null>(null);

function handleConfirm() {
  const result = cropperRef.value?.getResult() as CropperResult | undefined;
  if (!result?.canvas) return;

  result.canvas.toBlob(
    (blob) => {
      if (!blob) return;
      const file = new File([blob], "cropped.jpg", { type: "image/jpeg" });
      emit("confirm", file);
    },
    "image/jpeg",
    0.9,
  );
}
</script>

<template>
  <div class="fixed inset-0 z-50 flex items-center justify-center bg-black/60">
    <div class="flex flex-col gap-4 rounded-xl bg-white p-4 w-[90vw] max-w-2xl">
      <p class="font-extrabold">{{ t("fileUploader.cropTitle") }}</p>

      <Cropper
        ref="cropperRef"
        :src="props.imageSrc"
        :canvas="{ maxWidth: props.maxWidth, maxHeight: props.maxHeight }"
        :stencil-props="{
          aspectRatio: undefined, // free crop; set a number (e.g. 1) to lock a ratio
        }"
        :resize-image="{ adjustStencil: true }"
        class="h-[50vh]"
      />

      <p class="text-sm text-light-gray">
        {{ t("fileUploader.cropHint", { size: props.maxWidth }) }}
      </p>

      <div class="flex flex-row gap-4 justify-end">
        <CustomButton :title="t('common.cancel')" @click="emit('cancel')" />
        <CustomButton :title="t('common.confirm')" @click="handleConfirm" />
      </div>
    </div>
  </div>
</template>
